// Seam between the workspace UI and the browser call engine (F2, packages/call-client).
// The UI never fakes ringing, answer or audio: without a working engine and a server-issued
// bootstrap, the phone reports why it is not ready and every audio control stays absent.
import type { Call, Device, EventEnvelope } from '@bcio-voice/contracts';

export type MicPermission = 'unknown' | 'prompt' | 'granted' | 'denied' | 'unsupported';

export type PhoneState =
  | 'unavailable' // no engine for this device (none attached, not configured, or unsupported)
  | 'starting'
  | 'blocked'
  | 'needs_microphone'
  | 'in_another_tab'
  | 'connecting'
  | 'registered'
  | 'reconnecting'
  | 'offline'
  | 'stopped'
  | 'revoked';

export interface AudioDeviceView {
  id: string;
  label: string;
}

export interface BridgeMedia {
  permission: MicPermission;
  inputs: AudioDeviceView[];
  outputs: AudioDeviceView[];
  selectedInput: string | null;
  selectedOutput: string | null;
  outputSelection: 'supported' | 'unsupported';
  playback: 'idle' | 'playing' | 'blocked';
}

export interface BridgeCommand {
  kind: string;
  phase: string;
  error: { code: string; message: string } | null;
}

/** One call the engine tracks on this device. `callId` is null until POST /calls returns. */
export interface BridgeCall {
  key: string;
  callId: string | null;
  origin: 'outbound' | 'inbound';
  phase: string;
  /** The server call the engine last verified, used to seed the workspace cache. */
  server: Call | null;
  muted: boolean;
  microphone: 'none' | 'live' | 'muted' | 'lost';
  media: 'none' | 'connecting' | 'connected' | 'recovering' | 'lost';
  signaling: 'live' | 'degraded';
  remoteAudio: 'none' | 'attached' | 'playing' | 'blocked';
  endReason: string | null;
  error: { code: string; message: string; serverCode: string | null } | null;
  command: BridgeCommand | null;
  /** This browser's SIP WebSocket events since the call was created (see PhoneTransportView). */
  transportEvents?: TransportEventView[];
}

/**
 * Why the last phone connection attempt failed, as this browser saw it. `confidence` is `observed` when
 * the browser reported the fact directly (a close code from the server, a policy block, a timeout, a SIP
 * status) and `inferred` when it is worked out from what the browser exposes (every failed WebSocket
 * handshake looks the same to a page: close code 1006, no reason).
 */
export interface TransportFailureView {
  kind: string;
  confidence: 'observed' | 'inferred';
  attempt: number;
  /** Epoch ms the attempt failed, when known. */
  at: number | null;
  connectMs: number | null;
  closeCode: number | null;
  closeReason: string | null;
  wasClean: boolean | null;
  errorSeen: boolean;
  /** False when the browser refused to create the WebSocket; null when the engine had no socket report. */
  socketCreated: boolean | null;
  timedOut: boolean;
  /** A connect-src `securitypolicyviolation` named the SIP host for this attempt. */
  cspBlocked: boolean;
  sipStatus: number | null;
  /** The engine's failure code. */
  code: string | null;
}

/** One SIP WebSocket observation from this browser. Codes and numbers only: never credentials, SIP messages, SDP, cookies or tokens. */
export interface TransportEventView {
  sequence: number;
  at: number;
  kind: string;
  attempt: number;
  failure: string | null;
  closeCode: number | null;
  sipStatus: number | null;
  retryInMs: number | null;
}

/** The sanitized SIP WebSocket diagnostic for this browser's phone. */
export interface PhoneTransportView {
  /** `scheme://host:port/path`, token-like path segments masked. */
  endpoint: string | null;
  attempt: number;
  state: 'pending' | 'open' | 'registered' | 'failed' | null;
  lastFailure: TransportFailureView | null;
  consecutiveFailures: number;
  nextRetryAt: string | null;
  retryDelayMs: number | null;
  /** Oldest first, bounded. */
  recent: TransportEventView[];
}

export interface BridgeSnapshot {
  engine: 'none' | 'call-client';
  phone: {
    state: PhoneState;
    reason: string | null;
    attempt: number;
    nextRetryAt: string | null;
    acceptingCalls: boolean;
    issues: string[];
    /** Absent or null without a call engine, or before the first connection attempt. */
    transport?: PhoneTransportView | null;
  };
  bootstrap: { status: string; reasonCode: string | null; expiresAt: string | null } | null;
  media: BridgeMedia;
  calls: BridgeCall[];
}

export type BridgeResult = { ok: true } | { ok: false; code: string; message: string; serverCode: string | null };

export interface PlaceCallRequest {
  destination: Call['destination'];
  from_number_id?: string;
  contact_id?: string;
  calling_session?: {
    attempt_id: string;
    candidate_id?: number;
    controller_id?: string | null;
  };
}

export interface CallBridge {
  getSnapshot(): BridgeSnapshot;
  subscribe(listener: () => void): () => void;
  /** The server device for this session (with its canonical bootstrap when just issued), or null. */
  attachDevice(device: Device | null): void;
  /** Await complete detach before releasing profile ownership to another window. */
  releaseDevice?(): Promise<void>;
  /** Stop registration but keep the device (Settings "Turn off"); `start` resumes. */
  stop(): Promise<void>;
  start(): void;
  retryNow(): void;
  takeOver(): void;
  requestMicrophone(): Promise<BridgeResult>;
  refreshDevices(): Promise<void>;
  selectInput(id: string | null): Promise<BridgeResult>;
  selectOutput(id: string | null): Promise<BridgeResult>;
  resumeAudio(): Promise<void>;
  placeCall(request: PlaceCallRequest): Promise<BridgeResult & { key?: string }>;
  answer(key: string): Promise<BridgeResult>;
  decline(key: string): Promise<BridgeResult>;
  hangup(key: string): Promise<BridgeResult>;
  hold(key: string): Promise<BridgeResult>;
  resume(key: string): Promise<BridgeResult>;
  sendDtmf(key: string, digits: string): Promise<BridgeResult>;
  setMuted(key: string, muted: boolean): BridgeResult;
  dismiss(key: string): void;
  applyServerCall(call: Call): void;
  notifyServerEvent(event: EventEnvelope): void;
  /** Ends calls, unregisters and releases the microphone. */
  dispose(): Promise<void>;
  /**
   * The engine's microphone and remote audio tracks for a call, on attach, replacement and end
   * (desktop local capture). Optional: bridges without an audio engine have no tracks.
   */
  onTracks?(listener: (update: BridgeTracks) => void): () => void;
}

export interface BridgeTracks {
  callId: string | null;
  local: MediaStreamTrack | null;
  remote: MediaStreamTrack | null;
}

export const NO_MEDIA: BridgeMedia = {
  permission: 'unknown',
  inputs: [],
  outputs: [],
  selectedInput: null,
  selectedOutput: null,
  outputSelection: 'unsupported',
  playback: 'idle',
};

export const UNCONFIGURED_SNAPSHOT: BridgeSnapshot = {
  engine: 'none',
  phone: { state: 'unavailable', reason: 'call_engine_unavailable', attempt: 0, nextRetryAt: null, acceptingCalls: false, issues: [] },
  bootstrap: null,
  media: NO_MEDIA,
  calls: [],
};

const UNAVAILABLE: BridgeResult = { ok: false, code: 'call_engine_unavailable', message: 'This browser can’t connect call audio yet.', serverCode: null };

/**
 * The honest default: no audio engine. It records the server's bootstrap reason so the
 * dock can say why the phone is not ready, and refuses every audio action.
 */
export class UnconfiguredBridge implements CallBridge {
  private snapshot: BridgeSnapshot;
  private readonly listeners = new Set<() => void>();

  constructor(private readonly unavailableReason = 'call_engine_unavailable') {
    this.snapshot = { ...UNCONFIGURED_SNAPSHOT, phone: { ...UNCONFIGURED_SNAPSHOT.phone, reason: unavailableReason } };
  }

  getSnapshot(): BridgeSnapshot {
    return this.snapshot;
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  attachDevice(device: Device | null): void {
    const b = device?.bootstrap;
    const reason = !device ? 'no_device' : b?.status === 'unconfigured' ? b.reason_code : this.unavailableReason;
    const bootstrap = b ? { status: b.status, reasonCode: b.status === 'unconfigured' ? b.reason_code : null, expiresAt: b.status === 'issued' ? b.expires_at : null } : null;
    if (reason === this.snapshot.phone.reason && bootstrap?.status === this.snapshot.bootstrap?.status) return;
    this.snapshot = { ...this.snapshot, phone: { ...this.snapshot.phone, reason }, bootstrap };
    for (const l of this.listeners) l();
  }

  async stop(): Promise<void> {}
  start(): void {}
  retryNow(): void {}
  takeOver(): void {}
  async requestMicrophone(): Promise<BridgeResult> { return UNAVAILABLE; }
  async refreshDevices(): Promise<void> {}
  async selectInput(): Promise<BridgeResult> { return UNAVAILABLE; }
  async selectOutput(): Promise<BridgeResult> { return UNAVAILABLE; }
  async resumeAudio(): Promise<void> {}
  async placeCall(): Promise<BridgeResult> { return UNAVAILABLE; }
  async answer(): Promise<BridgeResult> { return UNAVAILABLE; }
  async decline(): Promise<BridgeResult> { return UNAVAILABLE; }
  async hangup(): Promise<BridgeResult> { return UNAVAILABLE; }
  async hold(): Promise<BridgeResult> { return UNAVAILABLE; }
  async resume(): Promise<BridgeResult> { return UNAVAILABLE; }
  async sendDtmf(): Promise<BridgeResult> { return UNAVAILABLE; }
  setMuted(_key: string, _muted: boolean): BridgeResult { return UNAVAILABLE; }
  dismiss(): void {}
  applyServerCall(): void {}
  notifyServerEvent(): void {}

  async dispose(): Promise<void> {
    this.listeners.clear();
  }
}

/** The engine call attached to a server call ID, if this device has one. */
export function engineCallFor(snapshot: BridgeSnapshot, callId: string): BridgeCall | null {
  const id = callId.toLowerCase();
  return snapshot.calls.find(c => c.callId?.toLowerCase() === id) ?? null;
}

const LIVE_LEG_PHASES = new Set(['requesting', 'unconfirmed', 'connecting', 'ringing', 'offered', 'answering', 'active', 'held', 'ending']);

export function bridgeCallLive(call: BridgeCall): boolean {
  return LIVE_LEG_PHASES.has(call.phase);
}
